<template>
  <transition name="cvm-fade">
    <div v-if="visible" class="cvm-overlay" @mousedown.self="$emit('close')">
      <div class="cvm-card">
        <!-- Header -->
        <div class="cvm-header">
          <div class="cvm-header-text">
            <h2 class="cvm-title">{{ title }}</h2>
            <p v-if="subtitle" class="cvm-subtitle">{{ subtitle }}</p>
          </div>
          <div class="cvm-header-actions">
            <button
              class="cvm-btn-download"
              :disabled="downloading || !contract || !contract.id"
              title="Download a printable copy with field placeholders"
              @click="downloadPrintableCopy"
            >
              <i :class="downloading ? 'fa fa-spinner fa-spin' : 'fa fa-download'"></i>
              {{ downloading ? 'Preparing…' : 'Download' }}
            </button>
            <button class="cvm-close" aria-label="Close" @click="$emit('close')">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M1 1l16 16M17 1L1 17" stroke="#555" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="cvm-body">
          <div v-if="loading" class="cvm-loading">
            <i class="fa fa-spinner fa-spin"></i>
            <p>Loading contract…</p>
          </div>

          <div v-else-if="loadError" class="cvm-error">
            <i class="fa fa-exclamation-triangle"></i>
            <p>{{ loadError }}</p>
          </div>

          <pdf-field-editor
            v-else
            party="client"
            mode="view"
            :existing-fields="fields"
            :initial-pdf-bytes="pdfBytes"
            :selected-field-type="null"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { Message } from 'element-ui';
import PdfFieldEditor from '@/components/Contracts/PdfFieldEditor.vue';

export default {
  name: 'ContractViewerModal',
  components: { PdfFieldEditor },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    contract: {
      type: Object,
      default: null,
    },
    leadEventId: {
      type: Number,
      required: true,
    },
    venueName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      loadError: '',
      pdfBytes: null,
      downloading: false,
    };
  },
  computed: {
    title() {
      return 'Contract Preview';
    },
    subtitle() {
      return this.venueName ? `As shown to the client for ${this.venueName}` : 'As shown to the client for signing';
    },
    fields() {
      return (this.contract && this.contract.signature_fields) || [];
    },
    viewableAttachment() {
      const attachments = (this.contract && this.contract.attachments) || [];
      return attachments.find(a => a.type === 'vc_signed_copy')
        || attachments.filter(a => a.type === 'initial_contract').pop()
        || attachments[0]
        || null;
    },
  },
  watch: {
    visible(v) {
      if (v) this.load();
      else this.reset();
    },
  },
  methods: {
    reset() {
      this.pdfBytes = null;
      this.loadError = '';
      this.loading = false;
      this.downloading = false;
    },
    async load() {
      this.reset();
      const attachment = this.viewableAttachment;
      if (!attachment) {
        this.loadError = 'No contract PDF available to preview.';
        return;
      }
      this.loading = true;
      try {
        const res = await this.$store.dispatch('contracts/getDownloadUrl', {
          leadEventId: this.leadEventId,
          attachmentId: attachment.id,
        });
        const url = (res && (res.download_url || res.url)) || res;
        const response = await fetch(url);
        const buffer = await response.arrayBuffer();
        this.pdfBytes = new Uint8Array(buffer);
      } catch (e) {
        this.loadError = 'Could not load the contract PDF. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async downloadPrintableCopy() {
      if (! this.contract || ! this.contract.id || this.downloading) return;
      this.downloading = true;
      try {
        const res = await this.$store.dispatch('contracts/getPrintableCopyUrl', {
          leadEventId: this.leadEventId,
          contractId: this.contract.id,
        });
        const url = (res && (res.download_url || res.url)) || res;
        if (! url) throw new Error('No download URL returned');
        window.open(url, '_blank', 'noopener');
      } catch (e) {
        Message.error('Could not generate printable copy. Please try again.');
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style scoped>
.cvm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.cvm-card {
  position: relative;
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 1100px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18);
}

.cvm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #eee;
  background: #fafafa;
}

.cvm-header-text {
  flex: 1;
  text-align: center;
  padding: 0 32px;
}

.cvm-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
  font-family: Georgia, 'Times New Roman', serif;
}

.cvm-subtitle {
  font-size: 13px;
  color: #888;
  margin: 4px 0 0;
}

.cvm-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  line-height: 1;
  opacity: 0.6;
  transition: opacity 0.15s;
}
.cvm-close:hover { opacity: 1; }

.cvm-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.cvm-btn-download {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1.5px solid #6f4cd6;
  background: #6f4cd6;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s;
}
.cvm-btn-download:hover:not(:disabled) {
  background: #5b3bbd;
  border-color: #5b3bbd;
}
.cvm-btn-download:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cvm-body {
  flex: 1;
  overflow: auto;
  background: #f3f4f6;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cvm-loading,
.cvm-error {
  padding: 80px 0;
  color: #555;
  text-align: center;
}

.cvm-loading i,
.cvm-error i {
  font-size: 28px;
  margin-bottom: 12px;
  display: block;
}

.cvm-loading p,
.cvm-error p {
  margin: 0;
  font-size: 14px;
}

.cvm-error {
  color: #b91c1c;
}

.cvm-fade-enter-active,
.cvm-fade-leave-active {
  transition: opacity 0.2s ease;
}
.cvm-fade-enter,
.cvm-fade-leave-to {
  opacity: 0;
}
</style>
